import { useMemo, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/v2/empty-state";
import { useEntityMedia } from "@/hooks/use-api";
import { GALLERY_TILE } from "@/lib/images";
import { JobPhoto } from "./job-photo";
import { PhotoLightbox } from "./photo-lightbox";
import { groupPhotosByStage, imageItemsFromSubmissions } from "./media-helpers";
import type { MediaItem } from "@adva/types";

const EMPTY_COPY: Record<"job" | "opportunity", string> = {
  job: "Job-site photos appear here once work is documented on this job.",
  opportunity:
    "Photos appear here once work is documented on this opportunity's jobs.",
};

function GallerySkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1].map((section) => (
        <div key={section} className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <div className="grid grid-cols-3 gap-2 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                className="aspect-[3/2] w-full rounded-[var(--radius-md)]"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Presentational gallery — stage-grouped grid + single lightbox. Split from
 * the data container so the grouping + render are unit-testable without a
 * QueryClient. */
export function JobPhotoGalleryView({
  items,
  loading,
  error = false,
  entityType,
}: {
  items: MediaItem[];
  loading: boolean;
  error?: boolean;
  entityType: "job" | "opportunity";
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { sections, flat } = useMemo(() => groupPhotosByStage(items), [items]);

  if (loading) return <GallerySkeleton />;

  // §1.5 — a failed fetch is a surface error, NOT the "No photos" empty state;
  // collapsing the two would tell the operator there's no evidence when the
  // request actually failed. Mirrors entity-activity-feed's isError branch.
  if (error) {
    return (
      <EmptyState
        title="Couldn't load photos"
        description="Refresh to try again."
      />
    );
  }

  if (flat.length === 0) {
    // Type 2 — relationship-not-yet-triggered (empty-states-that-teach §2). No
    // CTA in v1 (decision G1 — no in-app upload path; a dead button is a false
    // affordance).
    return (
      <EmptyState title="No photos" description={EMPTY_COPY[entityType]} />
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        {sections.map((section) => (
          <section key={String(section.stage)}>
            <h3 className="typo-label text-[color:var(--color-text-md)]">
              {section.label}
              <span className="text-[color:var(--color-text-lo)]">
                {" · "}
                {section.photos.length}
              </span>
            </h3>
            <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {section.photos.map((photo, i) => (
                <JobPhoto
                  key={photo.id}
                  item={photo}
                  slot={GALLERY_TILE}
                  onOpen={() => setLightboxIndex(section.startIndex + i)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <PhotoLightbox
        photos={flat}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </TooltipProvider>
  );
}

/** Data container — fetches an entity's media and renders the gallery. Job
 * detail uses the `by-entity/job/{id}` route; opportunity detail uses the
 * cross-child `by-opportunity-rollup/{id}` route (see `useEntityMedia`). */
export function JobPhotoGallery({
  entityType,
  entityId,
}: {
  entityType: "job" | "opportunity";
  entityId: string;
}) {
  const { data, isLoading, isError } = useEntityMedia(entityType, entityId);
  const items = useMemo(
    () => imageItemsFromSubmissions(data?.submissions),
    [data],
  );
  return (
    <JobPhotoGalleryView
      items={items}
      loading={isLoading}
      error={isError}
      entityType={entityType}
    />
  );
}
